import React, { useEffect, useState } from 'react';
import PeopleIcon from '@mui/icons-material/People';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

import { getAdminStats } from '../../../api/admin.api';
import { calculateStatChange } from '../../../utils/calculateStatChange';

export default function Dashboard() {
  const [statsData, setStatsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await getAdminStats();

        if (res.data) setStatsData(res.data);
      } catch (err) {
        setError('Failed to load stats.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, [navigate, error]);

  const totalChange = statsData
    ? calculateStatChange(statsData.total_teachers, statsData.total_last_month)
    : null;
  const pendingChange = statsData
    ? calculateStatChange(statsData.pending, statsData.pending_last_month)
    : null;
  const todayChange = statsData
    ? calculateStatChange(statsData.approved_today, statsData.approved_yesterday)
    : null;

  const stats = [
    {
      title: 'Total Teachers',
      value: isLoading ? '...' : (statsData?.total_teachers ?? 'N/A'),
      icon: <PeopleIcon />,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      change: totalChange?.label ?? 'N/A',
      isPositive: totalChange?.isPositive ?? true,
      vsLabel: 'vs last month',
    },
    {
      title: 'Pending Approvals',
      value: isLoading ? '...' : (statsData?.pending ?? 'N/A'),
      icon: <PendingIcon />,
      color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      change: pendingChange?.label ?? 'N/A',
      isPositive: pendingChange ? !pendingChange.isPositive : true,
      vsLabel: 'vs last month',
    },
    {
      title: 'Approved Today',
      value: isLoading ? '...' : (statsData?.approved_today ?? 'N/A'),
      icon: <CheckCircle />,
      color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      change: todayChange?.label ?? 'N/A',
      isPositive: todayChange?.isPositive ?? true,
      vsLabel: 'vs yesterday',
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-light mb-2">Dashboard Overview</h1>
        <p className="text-gray-400">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-gradient-glass backdrop-blur-sm p-5 rounded-xl shadow-lg border border-white/10 hover:border-primary/30 transition-all duration-300 hover:transform hover:scale-105"
          >
            <div className="flex items-start justify-between mb-3">
              <h2 className="text-sm text-gray-400 font-medium uppercase tracking-wide">
                {stat.title}
              </h2>
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center shadow-lg"
                style={{ background: stat.color }}
              >
                {React.cloneElement(stat.icon, {
                  sx: { color: 'white', fontSize: 28 },
                })}
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-light mb-1">{stat.value}</p>
                <div className="flex items-center gap-1">
                  <span
                    className={`text-sm font-semibold ${
                      stat.isPositive ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {stat.change}
                  </span>
                  <span className="text-xs text-gray-500">{stat.vsLabel}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
